import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { productApi } from '../shared/api/endpoints';
import { useWishlist } from '../shared/context/WishlistContext';
import { useAddToCart } from '../shared/hooks/useAddToCart';
import './ProductDetailPage.css';

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'materials', label: 'Materials' },
  { id: 'fit', label: 'Size & Fit' },
  { id: 'shipping', label: 'Shipping & Returns' },
];

const canInnerZoom = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState('');
  const [tab, setTab] = useState('details');
  const [error, setError] = useState('');
  const addToCart = useAddToCart();
  const { toggle, has } = useWishlist();
  const mainRef = useRef(null);

  const startZoom = (e) => {
    if (!canInnerZoom()) return;
    e.currentTarget.style.setProperty('--zoom-scale', '1.5');
  };

  const moveZoom = (e) => {
    if (!canInnerZoom()) return;
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--zoom-x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    e.currentTarget.style.setProperty('--zoom-y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  const endZoom = (e) => {
    e.currentTarget.style.removeProperty('--zoom-x');
    e.currentTarget.style.removeProperty('--zoom-y');
    e.currentTarget.style.removeProperty('--zoom-scale');
  };

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      if (!el.style.getPropertyValue('--zoom-scale')) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const x = parseFloat(el.style.getPropertyValue('--zoom-x') || '50');
      const y = parseFloat(el.style.getPropertyValue('--zoom-y') || '50');
      const nextX = Math.min(100, Math.max(0, x + (e.deltaX / Math.max(rect.width, 1)) * 80));
      const nextY = Math.min(100, Math.max(0, y + (e.deltaY / Math.max(rect.height, 1)) * 80));
      el.style.setProperty('--zoom-x', `${nextX}%`);
      el.style.setProperty('--zoom-y', `${nextY}%`);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [product]);

  useEffect(() => {
    setActiveImg(0);
    setTab('details');
    productApi
      .get(id)
      .then((res) => {
        const p = res.data.data;
        setProduct(p);
        setSize(p.sizesAvailable?.[0] || '');
        const cat = p.category?._id || p.category;
        if (cat) {
          productApi
            .list({ category: cat, limit: 8 })
            .then((r) => {
              const items = (r.data.data?.items || []).filter((x) => x._id !== p._id).slice(0, 4);
              setRelated(items);
            })
            .catch(() => setRelated([]));
        } else {
          setRelated([]);
        }
      })
      .catch(() => setError('Product not found'));
  }, [id]);

  const specs = useMemo(() => {
    if (!product) return [];
    return [
      ['Components', product.noOfComponents],
      ['Colour', product.colour],
      ['Fabric', product.materialFabric || product.fabricType],
      ['Fit', product.fit],
      ['Care', product.careTip],
      ['Embroidery', product.embroidery],
      ['Notes', product.remarks],
    ].filter(([, v]) => v && String(v).toLowerCase() !== 'nothng' && String(v).toLowerCase() !== 'nothing');
  }, [product]);

  const features = useMemo(() => {
    if (!product) return [];
    const list = [];
    if (product.fit) list.push(product.fit);
    if (product.materialFabric || product.fabricType) list.push(product.materialFabric || product.fabricType);
    if (product.embroidery) list.push(`Embroidery: ${product.embroidery}`);
    if (product.careTip) list.push(product.careTip);
    if (!list.length) {
      return ['Refined atelier finish', 'Premium feel fabric', 'Designed for festive & everyday wear'];
    }
    return list.slice(0, 4);
  }, [product]);

  if (error) {
    return (
      <div className="page-shell">
        <Navbar />
        <div className="container" style={{ padding: '80px 0', textAlign: 'center' }}>
          <p>{error}</p>
          <Link to="/products" className="btn btn-gold">Browse products</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="page-shell">
        <Navbar />
        <div className="container pdp-load">Loading piece…</div>
      </div>
    );
  }

  const images = product.images?.length ? product.images : [];
  const catName = product.category?.name;
  const subName = product.subCategory?.name;
  const onSale = product.actualPrice > product.sellingPrice && product.discount > 0;

  return (
    <div className="page-shell">
      <Navbar />
      <main className="pdp">
        <div className="pdp__wrap">
          <nav className="pdp__crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/products">Shop</Link>
            {catName && (
              <>
                <span>/</span>
                <Link to={`/products?category=${product.category._id || product.category}`}>{catName}</Link>
              </>
            )}
            <span>/</span>
            <em>{product.title}</em>
          </nav>

          <div className="pdp__hero">
            {images.length > 1 && (
              <div className="pdp__thumbs" aria-label="Product images">
                {images.map((img, i) => (
                  <button
                    key={`${img}-${i}`}
                    type="button"
                    className={i === activeImg ? 'is-active' : ''}
                    style={{ backgroundImage: `url(${img})` }}
                    onClick={() => setActiveImg(i)}
                    aria-label={`View image ${i + 1}`}
                  />
                ))}
              </div>
            )}

            <div className="pdp__main-wrap">
              <div
                ref={mainRef}
                className="pdp__main"
                onMouseEnter={startZoom}
                onMouseLeave={endZoom}
                onMouseMove={moveZoom}
              >
                {images[activeImg] && (
                  <img
                    className="pdp__main-img"
                    src={images[activeImg]}
                    alt={product.title}
                    draggable={false}
                  />
                )}
                {onSale && (
                  <span className="pdp__ribbon">
                    <em>−{product.discount}%</em>
                    <b>off</b>
                  </span>
                )}
              </div>
            </div>

            <div className="pdp__info">
              <div className="pdp__tags">
                {subName && <span className="pdp__pill">{subName}</span>}
                {product.isDeal && <span className="pdp__pill pdp__pill--hot">Deal</span>}
                <span className="pdp__code">{product.productCode}</span>
              </div>

              <h1>{product.title}</h1>
              {product.subtitle && <p className="pdp__sub">{product.subtitle}</p>}

              <div className="pdp__rating" aria-label="Customer rating">
                <span className="pdp__stars">★★★★★</span>
                <em>4.8</em>
                <span>(atelier favourites)</span>
              </div>

              <div className="pdp__price">
                <strong>₹{Number(product.sellingPrice).toLocaleString()}</strong>
                {onSale && (
                  <>
                    <s>₹{Number(product.actualPrice).toLocaleString()}</s>
                    <span className="pdp__off">{product.discount}% OFF</span>
                  </>
                )}
              </div>

              {product.description && (
                <p className="pdp__desc">{product.description}</p>
              )}

              {product.colour && (
                <div className="pdp__colour">
                  <p>
                    Colour: <strong>{product.colour}</strong>
                  </p>
                  <div className="pdp__swatches">
                    <button
                      type="button"
                      className="is-active"
                      style={{ background: product.colour }}
                      aria-label={product.colour}
                      title={product.colour}
                    />
                  </div>
                </div>
              )}

              {product.sizesAvailable?.length > 0 && (
                <div className="pdp__sizes">
                  <div className="pdp__sizes-head">
                    <p>
                      Size: <strong>{size || '—'}</strong>
                    </p>
                    <span className="pdp__guide">Size guide</span>
                  </div>
                  <div className="pdp__size-row">
                    {product.sizesAvailable.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={size === s ? 'is-active' : ''}
                        onClick={() => setSize(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="pdp__actions">
                <button type="button" className="pdp__cta" onClick={() => addToCart(product, size)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M4 6h2l1.4 9.2a1.5 1.5 0 0 0 1.5 1.3h8.4a1.5 1.5 0 0 0 1.5-1.2L20 8H7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="10" cy="20" r="1.2" fill="currentColor" />
                    <circle cx="17" cy="20" r="1.2" fill="currentColor" />
                  </svg>
                  Add to cart
                </button>
                <button
                  type="button"
                  className={`pdp__wish${has(product._id) ? ' is-on' : ''}`}
                  aria-label="Wishlist"
                  onClick={() => toggle(product)}
                >
                  ♥
                </button>
              </div>

              <div className="pdp__trust">
                <div>
                  <span className="pdp__trust-icon" aria-hidden>✦</span>
                  <strong>Free shipping</strong>
                  <em>Above ₹2,999</em>
                </div>
                <div>
                  <span className="pdp__trust-icon" aria-hidden>↺</span>
                  <strong>Easy returns</strong>
                  <em>30-day trial</em>
                </div>
                <div>
                  <span className="pdp__trust-icon" aria-hidden>◎</span>
                  <strong>Secure pay</strong>
                  <em>Encrypted</em>
                </div>
              </div>
            </div>
          </div>

          <section className="pdp__tabs-sec">
            <div className="pdp__tabs" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={tab === t.id ? 'is-active' : ''}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="pdp__tab-body">
              <div className="pdp__tab-copy">
                {tab === 'details' && (
                  <>
                    <p>
                      {product.description ||
                        'A signature Nayavarnam piece — crafted for presence, comfort, and lasting celebration.'}
                    </p>
                    <ul className="pdp__features">
                      {features.map((f) => (
                        <li key={f}>
                          <i />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {tab === 'materials' && (
                  <>
                    {specs.length ? (
                      <dl className="pdp__spec-list">
                        {specs.map(([label, value]) => (
                          <div key={label}>
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <p>Premium fabrics chosen for drape, breathability, and festive longevity.</p>
                    )}
                  </>
                )}
                {tab === 'fit' && (
                  <p>
                    {product.fit
                      ? `Fit: ${product.fit}. `
                      : 'Designed for a flattering silhouette. '}
                    Refer to the size guide and choose your regular size unless noted otherwise.
                  </p>
                )}
                {tab === 'shipping' && (
                  <p>
                    Free shipping on orders above ₹2,999. Easy 30-day atelier exchanges on eligible
                    pieces. Secure checkout with encrypted payments.
                  </p>
                )}
              </div>
              {images[1] && (
                <div
                  className="pdp__tab-media"
                  style={{ backgroundImage: `url(${images[1]})` }}
                >
                  <span>Atelier detail</span>
                </div>
              )}
            </div>
          </section>

          {related.length > 0 && (
            <section className="pdp__pair">
              <div className="pdp__pair-head">
                <h2>You may also like</h2>
                <Link to={`/products?category=${product.category?._id || product.category || ''}`}>
                  View all →
                </Link>
              </div>
              <div className="pdp__pair-track">
                {related.map((p) => (
                  <article key={p._id} className="pdp-pair-card">
                    <Link
                      to={`/products/${p._id}`}
                      className="pdp-pair-card__media"
                      style={{ backgroundImage: p.images?.[0] ? `url(${p.images[0]})` : undefined }}
                    />
                    <div className="pdp-pair-card__meta">
                      <div>
                        <h3>{p.title}</h3>
                        <strong>₹{Number(p.sellingPrice).toLocaleString()}</strong>
                      </div>
                      <button
                        type="button"
                        className={`pdp-pair-card__wish${has(p._id) ? ' is-on' : ''}`}
                        aria-label="Wishlist"
                        onClick={() => toggle(p)}
                      >
                        ♥
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
